import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  deleteQuoteLine,
  fetchPendingQuotes,
  fetchQuote,
  getActiveQuoteLines,
  normalizeQuoteLines,
  updateSingleQuoteLine,
} from "../utils/shopApi";
import {
  isQuoteEditable,
  isSidebarQuote,
  preserveQuoteStatusAfterLineEdit,
} from "../utils/quoteStatus";

const listeners = new Set();

export function notifyPendingQuotesChanged() {
  listeners.forEach((listener) => listener());
}

export function subscribePendingQuotesChanged(listener) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getLineKey(line) {
  return (
    line?.id ?? `${line?.product_id ?? line?.product?.id}-${line?.quantity}`
  );
}

function filterPendingQuotes(quotes) {
  return quotes.filter(
    (entry) =>
      isSidebarQuote(entry) && getActiveQuoteLines(entry.lines).length > 0,
  );
}

async function refreshQuoteInList(quoteId, setPendingQuotes, quoteBeforeEdit) {
  const freshQuote = normalizeQuoteLines(await fetchQuote(quoteId));
  const mergedQuote = normalizeQuoteLines(
    preserveQuoteStatusAfterLineEdit(quoteBeforeEdit, freshQuote),
  );
  setPendingQuotes((current) => {
    const next = current.map((entry) =>
      entry.id === quoteId ? mergedQuote : entry,
    );
    return filterPendingQuotes(next);
  });
  notifyPendingQuotesChanged();
  return mergedQuote;
}

function optimisticRemoveLine(setPendingQuotes, quoteId, lineId) {
  setPendingQuotes((current) =>
    filterPendingQuotes(
      current.map((entry) => {
        if (entry.id !== quoteId) return entry;
        return normalizeQuoteLines({
          ...entry,
          lines: (entry.lines || []).filter((line) => line.id !== lineId),
        });
      }),
    ),
  );
}

function optimisticUpdateQuantity(
  setPendingQuotes,
  quoteId,
  lineId,
  nextQuantity,
) {
  setPendingQuotes((current) =>
    filterPendingQuotes(
      current.map((entry) => {
        if (entry.id !== quoteId) return entry;
        if (nextQuantity < 1) {
          return normalizeQuoteLines({
            ...entry,
            lines: (entry.lines || []).filter((line) => line.id !== lineId),
          });
        }
        return normalizeQuoteLines({
          ...entry,
          lines: (entry.lines || []).map((line) =>
            line.id === lineId ? { ...line, quantity: nextQuantity } : line,
          ),
        });
      }),
    ),
  );
}

export function usePendingQuotes({ enabled = true } = {}) {
  const token = useSelector((state) => state.auth.token);
  const [pendingQuotes, setPendingQuotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [updatingKey, setUpdatingKey] = useState(null);
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    if (!token) {
      setPendingQuotes([]);
      return;
    }

    setLoading(true);
    setError("");
    try {
      const quotes = await fetchPendingQuotes();
      setPendingQuotes(filterPendingQuotes(quotes));
    } catch (loadError) {
      setPendingQuotes([]);
      setError(
        loadError?.message ||
          "Unable to load pending quote requests right now.",
      );
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!enabled || !token) {
      setPendingQuotes([]);
      return undefined;
    }

    refresh();
    const listener = () => {
      refresh();
    };
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, [enabled, token, refresh]);

  const updateLineQuantity = useCallback(async (quote, lineId, change) => {
    if (!isQuoteEditable(quote)) {
      throw new Error("This quote can no longer be edited.");
    }

    const lines = Array.isArray(quote?.lines) ? quote.lines : [];
    const line = lines.find((entry) => entry.id === lineId);
    if (!line) {
      throw new Error("Quote line not found.");
    }

    const nextQuantity = (Number(line.quantity) || 0) + change;
    const actionKey = `${quote.id}:${lineId}:qty`;
    const quoteBeforeEdit = quote;

    setUpdatingKey(actionKey);
    setError("");
    try {
      optimisticUpdateQuantity(
        setPendingQuotes,
        quote.id,
        lineId,
        nextQuantity,
      );
      if (nextQuantity < 1) {
        await deleteQuoteLine(quote.id, lineId);
      } else {
        await updateSingleQuoteLine(quote, lineId, nextQuantity);
      }
      return await refreshQuoteInList(
        quote.id,
        setPendingQuotes,
        quoteBeforeEdit,
      );
    } catch (updateError) {
      setError(
        updateError?.message || "Unable to update this quote right now.",
      );
      await refreshQuoteInList(quote.id, setPendingQuotes, quoteBeforeEdit);
      throw updateError;
    } finally {
      setUpdatingKey(null);
    }
  }, []);

  const removeLine = useCallback(async (quote, lineId) => {
    if (!isQuoteEditable(quote)) {
      throw new Error("This quote can no longer be edited.");
    }

    const actionKey = `${quote.id}:${lineId}:remove`;
    const quoteBeforeEdit = quote;
    setUpdatingKey(actionKey);
    setError("");
    try {
      optimisticRemoveLine(setPendingQuotes, quote.id, lineId);
      await deleteQuoteLine(quote.id, lineId);
      return await refreshQuoteInList(
        quote.id,
        setPendingQuotes,
        quoteBeforeEdit,
      );
    } catch (updateError) {
      setError(
        updateError?.message || "Unable to remove this quote line right now.",
      );
      await refreshQuoteInList(quote.id, setPendingQuotes, quoteBeforeEdit);
      throw updateError;
    } finally {
      setUpdatingKey(null);
    }
  }, []);

  const isLineUpdating = useCallback(
    (quoteId, lineId) =>
      updatingKey === `${quoteId}:${lineId}:qty` ||
      updatingKey === `${quoteId}:${lineId}:remove`,
    [updatingKey],
  );

  return {
    pendingQuotes,
    loading,
    error,
    setError,
    refresh,
    updateLineQuantity,
    removeLine,
    isLineUpdating,
    updatingKey,
    getLineKey,
    hasPendingQuotes: pendingQuotes.length > 0,
  };
}
